import { nameof } from "bindable-data";
import { Container, IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { StringFormatExtention } from "../../../../../../../extensions/StringFormatExtention";
import { GameFont } from "../../../../../common/GameFont";
import { FullBuildingTooltip } from "../../../../../common/components/FullBuildingTooltip";
import { CommonResourcesPackage } from "../../../../../common/resources/CommonResourcesPackage";
import { IMarketplaceBuildingModel } from "../../models/building/IMarketplaceBuildingModel";
import { PiggyMultiplier } from "../../../../../common/components/PiggyMultiplier";

export class BuildingDescriptionContainer extends Container {
    private readonly commonResources: CommonResourcesPackage;
    private readonly marketplaceBuildingModel: IMarketplaceBuildingModel;

    private tooltip: FullBuildingTooltip;
    private crownIcon: Sprite;
    private crownsText: Text;
    private piggyMultiplier: PiggyMultiplier;
    private piggyMultiplierText: Text;
    private dollarIcon: Sprite;
    private dollarText: Text;
    private boxIcon: Sprite;
    private boxText: Text;
    private tokenIcon: Sprite;
    private tokenText: Text;

    public constructor(
        commonResources: CommonResourcesPackage,
        marketplaceBuildingModel: IMarketplaceBuildingModel,
    ) {
        super();
        this.commonResources = commonResources;
        this.marketplaceBuildingModel = marketplaceBuildingModel;
        this.build();
        this.marketplaceBuildingModel.propertyChanged.add(this.onMarketplaceBuildingModelPropertyChanged, this);
    }

    private async onMarketplaceBuildingModelPropertyChanged(model: IMarketplaceBuildingModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "currentBuilding"):
                this.tokenIcon?.destroy();
                this.tokenText?.destroy();
                this.tokenIcon = null;
                this.tokenText = null;
                const currentBuilding = model.currentBuilding.value.building;
                this.crownsText.text = StringFormatExtention.formatWithComma(currentBuilding.initialVisitCostInCoins);
                this.piggyMultiplierText.text = `+${Math.round(((currentBuilding.piggyMultiplier - 1) * 100))}%`;
                this.dollarText.text = StringFormatExtention.formatWithComma(currentBuilding.costPerSqft);
                this.boxText.text = StringFormatExtention.formatWithComma(currentBuilding.sqft);

                this.piggyMultiplier.alpha = model.currentBuilding.value.building.piggyMultiplier != 1 ? 1 : 0;
                this.piggyMultiplierText.alpha = this.piggyMultiplier.alpha;
                this.piggyMultiplier.eventMode = model.currentBuilding.value.building.piggyMultiplier != 1 ? "static" : "none";
                this.piggyMultiplierText.eventMode = this.piggyMultiplier.eventMode;
                break;
        }
    }

    private build(): void {
        this.piggyMultiplier = new PiggyMultiplier(this.commonResources);
        this.piggyMultiplier.eventMode = "static";
        this.piggyMultiplier.on("pointerdown", () => this.showTooltipByClick("PIGGY BOOSTER,\nEXCLUSIVE TO BUILDING OWNERS", 1, true));
        this.piggyMultiplier.scale.set(0.8);

        this.piggyMultiplierText = new Text("+0%", new FloorTextStyle());
        this.piggyMultiplierText.eventMode = "static";
        this.piggyMultiplierText.on("pointerdown", () => this.showTooltipByClick("PIGGY BOOSTER,\nEXCLUSIVE TO BUILDING OWNERS", 1, true));
        this.piggyMultiplierText.position.set(this.piggyMultiplier.x + this.piggyMultiplier.width + 7, 16);

        this.crownIcon = new Sprite(this.commonResources.assets_common_ui_substract_icon_png);
        this.crownIcon.eventMode = "static";
        this.crownIcon.on("pointerdown", () => this.showTooltipByClick("MAXIMUM NUMBER OF GAME COINS PER VISIT", 2, true));
        this.crownIcon.position.y = 9.5;
        this.crownIcon.position.set(this.piggyMultiplierText.x + this.piggyMultiplierText.width + 57, 9.5);

        this.crownsText = new Text("1000", new CrownsTextStyle());
        this.crownsText.eventMode = "static";
        this.crownsText.on("pointerdown", () => this.showTooltipByClick("MAXIMUM NUMBER OF GAME COINS PER VISIT", 2, true));
        this.crownsText.position.set(this.crownIcon.x + this.crownIcon.width + 7, 14);

        this.dollarIcon = new Sprite(this.commonResources.assets_common_ui_box_dollar_icon_png);
        this.dollarIcon.eventMode = "static";
        this.dollarIcon.on("pointerdown", () => this.showTooltipByClick("COST PER SQUARE FEET IN USD", 3));
        this.dollarIcon.position.y = 9;
        this.dollarIcon.position.x = this.crownsText.x + this.crownsText.width + 57;

        this.dollarText = new Text("0,25", new FloorTextStyle());
        this.dollarText.eventMode = "static";
        this.dollarText.on("pointerdown", () => this.showTooltipByClick("COST PER SQUARE FEET IN USD", 3));
        this.dollarText.position.set(this.dollarIcon.x + this.dollarIcon.width + 7, 16);

        this.boxIcon = new Sprite(this.commonResources.assets_common_ui_box_icon_png);
        this.boxIcon.eventMode = "static";
        this.boxIcon.on("pointerdown", () => this.showTooltipByClick("TOTAL SQUARE FEET IN THE BUILDING", 4));
        this.boxIcon.position.set(this.dollarText.x + this.dollarText.width + 57, 6.5);

        this.boxText = new Text("6,520", new FloorTextStyle());
        this.boxText.eventMode = "static";
        this.boxText.on("pointerdown", () => this.showTooltipByClick("TOTAL SQUARE FEET IN THE BUILDING", 4));
        this.boxText.position.set(this.boxIcon.x + this.boxIcon.width + 7, 17);

        this.tooltip = new FullBuildingTooltip(this.commonResources);
        this.tooltip.position.set(318, -85);

        this.addChild(
            this.piggyMultiplier,
            this.piggyMultiplierText,
            this.crownIcon,
            this.crownsText,
            this.dollarIcon,
            this.dollarText,
            this.boxIcon,
            this.boxText,
            this.tooltip
        );
    }

    public showWithToken(tokenValue: number): number {
        this.tokenText = new Text(tokenValue, new FloorTextStyle());
        this.tokenText.position.set(this.piggyMultiplier.x - this.tokenText.width - 57, 19);
        this.tokenIcon = new Sprite(this.commonResources.assets_common_ui_button_token_icon_png);
        this.tokenIcon.position.set(this.tokenText.x - this.tokenIcon.width - 7, 2);
        this.addChild(this.tokenText, this.tokenIcon);
        const tokenFieldWidth = this.tokenText.width + this.tokenIcon.width + 64;
        return tokenFieldWidth;
    }

    private async showTooltipByClick(tooltipText: string, itemNumber: number, needChangeColor?: boolean): Promise<void> {
        if (!this.tooltip.visible) {
            await this.tooltip.hideHint();
        }
        if (needChangeColor) {
            this.tooltip.changeTooltipTextColor("#ffc700");
        }
        this.tooltip.showHint(tooltipText, itemNumber);
    }

    public async hideContainer(): Promise<void> {
        await this.tooltip.hideHint();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.crownIcon.off("pointerdown");
        this.crownsText.off("pointerdown");
        this.piggyMultiplier.off("pointerdown");
        this.piggyMultiplierText.off("pointerdown");
        this.dollarIcon.off("pointerdown");
        this.dollarText.off("pointerdown");
        this.boxIcon.off("pointerdown");
        this.boxText.off("pointerdown");
        this.marketplaceBuildingModel.propertyChanged.remove(this.onMarketplaceBuildingModelPropertyChanged, this);
        super.destroy(options);
    }
}

class FloorTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 42
        }));
    }
}

class CrownsTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffc700",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 42
        }));
    }
}
